import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { ErrorBoundary as ChatErrorBoundary } from '@chat/components/error-boundary'
import { logger } from '@chat/services/logger'
import { useForm, getFormProps } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { Button, Text } from '@optechai/design-system'
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  SerializeFrom,
} from '@remix-run/node'
import {
  type ClientLoaderFunctionArgs,
  isRouteErrorResponse,
  useFetcher,
  useLoaderData,
  useLocation,
  useParams,
  useRouteError,
  useSubmit,
} from '@remix-run/react'
import {
  captureRemixErrorBoundaryError,
  setContext,
  captureException,
  withScope,
} from '@sentry/remix'
import { TRPCClientError } from '@trpc/client'
import { motion } from 'framer-motion'
import { AnimatePresence } from 'framer-motion'
import { observer } from 'mobx-react-lite'
import { z } from 'zod'

import { loggableError } from '@server/helpers/errors'
import type { TicketId } from '@server/models/ticket/ticket.dto'
import { CsatForm } from '@web/components/csat-form/csat-form'
import { CustomisationContext } from '@web/components/message/context'
import { MessageThread } from '@web/components/message/messages'
import { PoweredBy } from '@web/components/powered-by/powered-by'
import { SendBar } from '@web/components/send-bar/send-bar'
import { TicketContext } from '@web/components/tickets/ticket/context'
import {
  usePendingMessage,
  useTicketUpdates,
} from '@web/routes/__app/lab/conversation.($ticketId)/utils'
import { FeatureFlagContext, useFeatureFlag } from '@web/services/feature-flag'
import { uploadFile } from '@web/services/file/upload.server'

import { ChatHeader } from '../components/chat-header'
import { ThreadShell } from '../components/shell'
import { validatePublicKey } from '../helpers/auth'
import { ChatWidgetContext } from '../helpers/widget-context'
import { SessionJWT } from '../services/session/jwt.server'
import { trpc } from '../services/trpc/client'

export const loader = async (args: LoaderFunctionArgs) => {
  logger.log('GET /tickets/$publicKey/$ticketId', {
    url: args.request.url,
    params: args.params,
  })

  const { request } = args
  const url = new URL(request.url)

  const { publicKey, ticketId } = args.params

  const customerFirstName = url.searchParams.get('customer_first_name')
  const customerLastName = url.searchParams.get('customer_last_name')
  const session = url.searchParams.get('session')

  if (!publicKey) {
    throw new Response('Public key must be provided', { status: 400 })
  }

  if (!ticketId) {
    throw new Response('Ticket ID must be provided', { status: 400 })
  }

  const jwt = url.searchParams.get('session')
  const sessionJWT = new SessionJWT(ticketId as TicketId, jwt)
  const error = sessionJWT.verify()

  if (error) {
    logger.error('Invalid session token (invalid access)', {
      publicKey,
      ticketId,
    })
    // throw new Response('Invalid session token', { status: 403 })
  }

  const { subscriberId } = await validatePublicKey(publicKey)

  // Set the subscriber context for Sentry
  setContext('subscriber', {
    id: subscriberId,
    publicKey,
  })

  try {
    const [
      { messages, status, events, customer, actions, ticket, config },
      featureFlags,
    ] = await Promise.all([
      trpc.chatWidget.getMessageHistoryStatusAndConfig.query(
        {
          publicKey,
          ticketId,
        },
        { context: { publicKey, subscriberId } },
      ),
      trpc.subscriberSetup.getFeatureFlags.query(
        { subscriberId },
        { context: { publicKey, subscriberId } },
      ),
    ])

    return {
      customerFirstName: customerFirstName || customer?.firstName,
      customerLastName: customerLastName || customer?.lastName,
      messages,
      session,
      status,
      actions,
      ticket,
      publicKey,
      events,
      featureFlags,
      allowSendOnEscalation:
        config && config.integrationType === 'ChatWidget'
          ? config.allowSendOnEscalation
          : false,
      collectCsatBeforeClose:
        config && config.integrationType === 'ChatWidget'
          ? config.collectCsatBeforeClose
          : true,
      requestCsatAfterInactivity:
        config && config.integrationType === 'ChatWidget'
          ? config.requestCsatAfterInactivity
          : false,
      proactiveFeedbackTimeoutMs:
        config && config.integrationType === 'ChatWidget'
          ? config.proactiveFeedbackTimeoutMs
          : 60000,
    }
  } catch (error) {
    logger.error('Error loading ticket', {
      publicKey,
      ticketId,
      error,
      loggableError: loggableError(error),
    })
    if (error instanceof TRPCClientError && error.data?.code === 'NOT_FOUND') {
      throw new Response(error.message, { status: 404 })
    } else {
      captureException(error, {
        tags: {
          captureLocation: 'loader',
          remixRoute: 'tickets.$publicKey.$ticketId',
          publicKey: args.params.publicKey,
          ticketId: args.params.ticketId,
        },
      })
      throw error
    }
  }
}

const NewMessageSchema = z.object({
  customerFirstName: z.string().optional(),
  customerLastName: z.string().optional(),
  message: z.string().optional().default(''),
})

export const action = async (args: ActionFunctionArgs) => {
  // await getDataFromSession(args)

  logger.log('POST /ticket/$publicKey/$ticketId', {
    url: args.request.url,
    params: args.params,
  })

  if (args.params.ticketId === undefined) {
    throw new Error('Remote ID must be provided')
  }

  if (args.params.publicKey === undefined) {
    throw new Error('Public key must be provided')
  }

  const { publicKey, ticketId } = args.params

  const formData = await args.request.formData()
  const submission = parseWithZod(formData, { schema: NewMessageSchema })

  if (submission.status !== 'success') {
    logger.error('Invalid form submission', {
      submission,
    })
    return submission.reply()
  }

  /**
   * Note this is to allow the optional value to be submitted but
   * not cause the form to error.
   *
   * Without this check when the form revalidates it could
   * cause the whole page to redirect.
   */
  if (!submission.payload.message) {
    return submission.reply()
  }

  try {
    // Extract file directly from FormData to avoid Zod validation issues
    const maybeFile = await uploadFile(formData.get('file') as File)

    const { subscriberId } = await validatePublicKey(publicKey)
    await trpc.chatWidget.addMessageToTicket.mutate(
      {
        ...submission.value,
        publicKey,
        attachments: maybeFile ? [maybeFile] : [],
        ticketId,
      },
      { context: { publicKey, subscriberId } },
    )
    return null
  } catch (error) {
    if (error instanceof TRPCClientError && error.data?.code === 'NOT_FOUND') {
      throw new Response(error.message, { status: 404 })
    } else {
      captureException(error, {
        tags: {
          captureLocation: 'action',
          remixRoute: 'tickets.$publicKey.$ticketId',
          publicKey: args.params.publicKey,
          ticketId: args.params.ticketId,
        },
      })
      throw error
    }
  }
}

// Client loaders are only called on subsequent loads. We wrap the server loader
// in a try/catch and swallow it to make sure that the client can gracefully
// handle the server being offline or temporarily unreachable.
export async function clientLoader(
  args: ClientLoaderFunctionArgs,
): Promise<SerializeFrom<typeof loader> | null> {
  try {
    const serverLoaderData = await args.serverLoader<typeof loader>()
    return serverLoaderData
  } catch (error) {
    captureException(error, {
      tags: {
        captureLocation: 'clientLoader',
        remixRoute: 'tickets.$publicKey.$ticketId',
        publicKey: args.params.publicKey,
        ticketId: args.params.ticketId,
      },
      // This error is silently handled by the route and we can recover from this
      level: 'warning',
    })
    return null
  }
}

const EMPTY_FEATURE_FLAGS = {}
export default function Index() {
  const loaderData = useLoaderData<typeof clientLoader>()

  useEffect(() => {
    if (loaderData?.session) {
      window.parent.postMessage(
        {
          type: 'persistLorikeetSession',
          session: loaderData.session,
        },
        '*',
      )
    }
    if (loaderData?.ticket.id) {
      window.parent.postMessage(
        {
          type: 'persistLorikeetTicketId',
          ticketId: loaderData.ticket.id,
        },
        '*',
      )
    }
  }, [loaderData?.ticket.id, loaderData?.session])

  return (
    <FeatureFlagContext.Provider
      value={loaderData?.featureFlags ?? EMPTY_FEATURE_FLAGS}
    >
      <ThreadShell ticket={loaderData?.ticket ?? undefined}>
        <PageContent />
      </ThreadShell>
    </FeatureFlagContext.Provider>
  )
}

const PageContent = observer(() => {
  const location = useLocation()
  const loaderData = useLoaderData<typeof clientLoader>()
  // The initial loader data comes from the server loader which can never be null
  // because otherwise it would have thrown an error. It's only subsequent loads that uses
  // the client loader that can return null.
  const [successfulLoaderData, setSuccessfulLoaderData] = useState<
    NonNullable<typeof loaderData>
  >(loaderData!)
  const {
    messages: loaderMessages,
    status,
    ticket,
    actions: loaderActions,
    events: loaderEvents,
    customerFirstName,
    customerLastName,
    allowSendOnEscalation,
    collectCsatBeforeClose,
    requestCsatAfterInactivity,
    proactiveFeedbackTimeoutMs,
  } = successfulLoaderData

  useEffect(() => {
    if (loaderData) {
      setSuccessfulLoaderData(loaderData)
    }
  }, [loaderData])

  const responseStreamingEnabled = useFeatureFlag('rag.stream-response')
  const enableSseTicketUpdates = useFeatureFlag(
    'chat.enable-sse-ticket-updates',
  )

  // Tickets in chat widget are created when it's first loaded so if the user
  // never said anything, we don't have to watch for new updates
  const pendingMessage = usePendingMessage()
  const conversationStarted =
    loaderMessages.some((message) => message.messageType === 'CUSTOMER') ||
    !!pendingMessage
  const enableSse =
    !!ticket.id && !!enableSseTicketUpdates && conversationStarted
  const {
    messages: savedMessages,
    events,
    actions,
    pollingState,
  } = useTicketUpdates({
    enableSse,
    sseRoute: `${location.pathname}/sse`,
    loaderMessages,
    loaderEvents,
    loaderActions,
    responseStreamingEnabled,
  })

  const messages = useMemo(() => {
    return pendingMessage ? [...savedMessages, pendingMessage] : savedMessages
  }, [savedMessages, pendingMessage])

  const ticketContext = useContext(TicketContext)
  const chatWidgetState = useContext(ChatWidgetContext)
  const [showCsatDialog, setShowCsatDialog] = useState(false)
  const [requestedCsatBeforeClose, setRequestedCsatBeforeClose] =
    useState(false)
  const isReadOnly = ticketContext?.isReadOnly
  const hasBotResponse = useMemo(() => {
    const customerMessage = messages.find(
      (message) => message.messageType === 'CUSTOMER',
    )
    return messages.some(
      (message) =>
        message.messageType === 'BOT_RESPONSE' &&
        !!message.content.length &&
        message.createdAt &&
        customerMessage?.createdAt &&
        new Date(message.createdAt).getTime() >
          new Date(customerMessage.createdAt).getTime(),
    )
  }, [messages])
  const eligibleForCsat = !isReadOnly && hasBotResponse

  const handleClose = () => {
    // Skip CSAT collection if any of these conditions are met:
    // - Already requested CSAT for this session
    // - Customer not eligible for CSAT (no bot response or read-only ticket)
    // - CSAT already provided for this ticket
    // - CSAT collection disabled in configuration
    if (
      requestedCsatBeforeClose ||
      !eligibleForCsat ||
      !!ticketContext?.csatScore ||
      !collectCsatBeforeClose
    ) {
      chatWidgetState.closeWidget()
      return
    }

    setRequestedCsatBeforeClose(true)
    setShowCsatDialog(true)
  }

  const [proactivelyRequestFeedback, setProactivelyRequestFeedback] =
    useState(false)
  const lastUnacknowledgedBotResponse = useMemo(() => {
    const sortedMessages = [...messages].sort(
      (a, b) =>
        new Date(a.createdAt ?? '').getTime() -
        new Date(b.createdAt ?? '').getTime(),
    )
    const botResponse = sortedMessages.findLast(
      (message, index) => message.messageType === 'BOT_RESPONSE' && index > 0,
    )
    if (!botResponse) {
      return null
    }

    const customerMessageAfter = sortedMessages.filter(
      (message) =>
        message.messageType === 'CUSTOMER' &&
        message.createdAt &&
        botResponse.createdAt &&
        new Date(message.createdAt).getTime() >
          new Date(botResponse.createdAt).getTime(),
    )
    return !customerMessageAfter.length ? botResponse.id : null
  }, [messages])
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    if (lastUnacknowledgedBotResponse && requestCsatAfterInactivity) {
      timeoutId = setTimeout(() => {
        // If the ticket has been escalated, we don't want to proactively request feedback
        setProactivelyRequestFeedback(
          status !== 'Escalated' && status !== 'EscalatedFailed',
        )
      }, proactiveFeedbackTimeoutMs)
    } else {
      setProactivelyRequestFeedback(false)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [
    lastUnacknowledgedBotResponse,
    requestCsatAfterInactivity,
    proactiveFeedbackTimeoutMs,
    status,
  ])
  const feedbackRequested = useMemo(
    () =>
      actions.actionsTaken.some(
        (action) => action.action.actionType === 'GatherFeedback',
      ),
    [actions.actionsTaken],
  )

  // Do not automatically scroll when new messages arrive to allow users
  // to review previous messages at their own pace.
  const scrollRef = useRef<HTMLDivElement>(null)

  const showSendBar = !(
    status === 'EscalatedFailed' ||
    (status === 'Escalated' && !allowSendOnEscalation)
  )

  const customisation = useContext(CustomisationContext)

  useLogTimeToFirstBotResponse({
    messages,
    publicKey: successfulLoaderData.publicKey,
    ticketId: successfulLoaderData.ticket.id,
  })

  return (
    <div className="relative flex h-full flex-col">
      <ChatHeader
        customisations={customisation ?? {}}
        onClose={handleClose}
        onNewChat={chatWidgetState.newChat}
      />
      <div
        className="min-h-0 w-full flex-1 overflow-y-auto scroll-smooth"
        ref={scrollRef}
      >
        <MessageThread
          actions={{
            actionsTaken: [],
            skippedActions: [],
            actionError: undefined,
          }}
          events={events.filter((event) => event.type === 'THINKING')}
          hasEscalated={events.some(
            (event) => event.escalationReason !== undefined,
          )}
          invertMessageAlignment
          isLoading={
            pollingState === 'active' &&
            // This is a hack to avoid showing the loading spinner when we're
            // not actually processing the ticket anymore (e.g. because it's
            // been escalated)
            (status === 'Unprocessed' ||
              status === 'Processing' ||
              status === 'Responded')
          }
          messages={messages}
        />
        <AnimatePresence>
          {(feedbackRequested || proactivelyRequestFeedback) && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 flex w-full flex-col gap-y-m text-center"
              exit={{ opacity: 0, y: 8 }}
              initial={{ opacity: 0, y: 8 }}
              transition={{ type: 'spring', duration: 0.5 }}
            >
              {!ticketContext?.csatScore && (
                <Text color="subtle" variant="p-xs">
                  Rate your conversation
                </Text>
              )}
              <CsatForm />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="flex-shrink-0">
        <NewMessageForm
          customerFirstName={customerFirstName}
          customerLastName={customerLastName}
          showSendBar={showSendBar}
          useSubtleBranding={!!customisation?.useSubtleBranding}
        />
      </div>
      <AnimatePresence>
        {showCsatDialog && (
          <CsatOverlay
            onClose={() => setShowCsatDialog(false)}
            onRefuse={() => {
              chatWidgetState.closeWidget()
              setShowCsatDialog(false)
              // If they open the dialog again, we should ask them again
              setRequestedCsatBeforeClose(false)
            }}
            onSubmit={() => {
              chatWidgetState.closeWidget()
              setShowCsatDialog(false)
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
})

function NewMessageForm({
  customerFirstName,
  customerLastName,
  showSendBar,
  useSubtleBranding,
}: {
  customerFirstName: string | undefined
  customerLastName: string | undefined
  showSendBar: boolean
  useSubtleBranding: boolean
}) {
  const disableAttachments = useFeatureFlag('chat.disable-attachments')
  const actionData = useFetcher<typeof action>({ key: 'sendChat' })
  const [form] = useForm<z.infer<typeof NewMessageSchema>>({
    onValidate: ({ formData }) => {
      return parseWithZod(formData, {
        schema: NewMessageSchema,
      })
    },
    lastResult: actionData.state === 'idle' ? actionData.data : null,
    defaultValue: {
      customerFirstName,
      customerLastName,
      message: '',
    },
  })

  return (
    <actionData.Form
      className="w-full"
      encType="multipart/form-data"
      method="POST"
      {...getFormProps(form)}
    >
      {customerFirstName && (
        <input
          defaultValue={customerFirstName}
          name="customerFirstName"
          type="hidden"
        />
      )}
      {customerLastName && (
        <input
          defaultValue={customerLastName}
          name="customerLastName"
          type="hidden"
        />
      )}
      {showSendBar && (
        <SendBar
          allowEmptySubmit={false}
          disableAttachments={disableAttachments}
        />
      )}
      {useSubtleBranding && <PoweredBy />}
    </actionData.Form>
  )
}

const CsatOverlay = observer(
  ({
    onClose,
    onRefuse,
    onSubmit,
  }: {
    onClose: () => void
    onRefuse: () => void
    onSubmit: () => void
  }) => {
    const csatDialogRef = useRef<HTMLDivElement>(null)
    const ticketContext = useContext(TicketContext)
    return (
      <motion.div
        animate={{ opacity: 1 }}
        className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center bg-gray-300/80 backdrop-blur-sm"
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        onClick={(e) => {
          if (csatDialogRef.current?.contains(e.target as Node)) {
            return
          }

          onClose()
        }}
      >
        <motion.div
          animate={{ opacity: 1, marginTop: 0 }}
          className="w-80 rounded-md bg-surface p-8 shadow-lg"
          exit={{ opacity: 0, marginTop: 50 }}
          initial={{ opacity: 0, marginTop: 50 }}
          ref={csatDialogRef}
          transition={{ type: 'spring', duration: 0.5 }}
        >
          <h2 className="mb-4 text-center font-heading-h2">
            We'd love your feedback before you leave.
          </h2>
          <CsatForm onSubmit={onSubmit} />
          {!ticketContext?.csatScore && (
            <div className="mt-4 flex justify-center">
              <Button
                className="text-subtle"
                onClick={onRefuse}
                size="small"
                variant="link"
              >
                No thanks
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    )
  },
)

/**
 * Reserved for route error handling
 */
export const ErrorBoundary = () => {
  const error = useRouteError()
  const isRouteError = isRouteErrorResponse(error)
  const params = useParams()
  const customisation = useContext(CustomisationContext)
  const chatWidgetState = useContext(ChatWidgetContext)

  withScope((scope) => {
    scope.setTag('captureLocation', 'errorBoundary')
    scope.setTag('remixRoute', 'tickets.$publicKey.$ticketId')
    scope.setTag('publicKey', params.publicKey)
    scope.setTag('ticketId', params.ticketId)
    scope.setTag('isRouteError', isRouteError.toString())
    // If an errors bubbled up here we have crashed the page
    scope.setLevel('fatal')

    captureRemixErrorBoundaryError(error)
  })

  useEffect(() => {
    window.parent.postMessage(
      {
        type: 'error',
        error: isRouteError ? 'UNKNOWN_PUBLIC_KEY' : 'FATAL_ERROR',
      },
      '*',
    )
  }, [])

  return (
    <div>
      <ChatHeader
        customisations={customisation ?? {}}
        onClose={chatWidgetState.closeWidget}
        onNewChat={chatWidgetState.newChat}
      />
      <ErrorMessage error={error} isRouteError={isRouteError} />
    </div>
  )
}

function ErrorMessage({
  isRouteError,
  error,
}: {
  isRouteError: boolean
  error: unknown
}) {
  if (
    isRouteError &&
    error &&
    typeof error === 'object' &&
    'status' in error &&
    error.status === 404
  ) {
    return (
      <ChatErrorBoundary error="UNKNOWN_PUBLIC_KEY" title="Configuration error">
        We don't recognize this chat instance. Looks like a misconfiguration.
      </ChatErrorBoundary>
    )
  }

  if (
    isRouteError &&
    error &&
    typeof error === 'object' &&
    'status' in error &&
    error.status === 403
  ) {
    return (
      <ChatErrorBoundary error="INVALID_SESSION" title="Invalid session">
        The session token is invalid or missing. Viewing this page is not
        allowed.
      </ChatErrorBoundary>
    )
  }

  return (
    <ChatErrorBoundary error="FATAL_ERROR" title="Something went wrong">
      We're not sure what went wrong, but the chat instance is in a bad state.
    </ChatErrorBoundary>
  )
}

interface Message {
  createdAt?: string
  messageType: string
  remoteCreatedAt?: string
}

interface UseLogTimeToFirstBotResponseProps {
  messages: Message[]
  publicKey: string
  ticketId: TicketId
}

function useLogTimeToFirstBotResponse({
  messages,
  publicKey,
  ticketId,
}: UseLogTimeToFirstBotResponseProps) {
  const sessionStartTime = useMemo(() => Date.now(), [])
  const [hasLogged, setHasLogged] = useState(false)
  const submit = useSubmit()

  const firstUserMessageTime = useMemo(() => {
    const firstUserMessage = messages.find(
      (msg) => msg.messageType === 'CUSTOMER',
    )
    if (
      !firstUserMessage ||
      !(firstUserMessage.createdAt || firstUserMessage.remoteCreatedAt) ||
      (firstUserMessage.createdAt &&
        new Date(firstUserMessage.createdAt).getTime() < sessionStartTime)
    ) {
      return null
    }

    const sentTime = new Date(
      firstUserMessage.createdAt || firstUserMessage.remoteCreatedAt || '',
    )
    return sentTime.getTime()
  }, [messages])

  // Track when the first bot response is displayed and log the duration
  useEffect(() => {
    if (!firstUserMessageTime || hasLogged) {
      return
    }

    const firstBotResponse = messages.find(
      (msg) =>
        msg.messageType === 'BOT_RESPONSE' &&
        new Date(msg.createdAt || msg.remoteCreatedAt || '').getTime() >
          firstUserMessageTime,
    )
    if (!firstBotResponse) {
      return
    }

    const botTime = new Date(
      firstBotResponse.createdAt || firstBotResponse.remoteCreatedAt || '',
    ).getTime()
    const durationMs = botTime - firstUserMessageTime
    if (durationMs <= 0) {
      return
    }

    submit(
      { publicKey, ticketId, durationMs },
      {
        method: 'post',
        action: '/metrics/time-to-first-response-seen',
        navigate: false,
      },
    )
    setHasLogged(true)
  }, [messages, hasLogged, publicKey, ticketId, submit])
}
